import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../utils';
import { Button } from '../inputs/button';
import { Select } from '../inputs/select';

export interface DataTablePaginationLabels {
  /** e.g. "1–100 sur 312" */
  range: (from: number, to: number, total: number) => string;
  /** Shown instead of `range` when the total is unknown. */
  pageOnly: (page: number) => string;
  rowsPerPage: string;
  previous: string;
  next: string;
  goToPage: (page: number) => string;
}

export interface DataTablePaginationProps {
  /** 1-based. */
  page: number;
  pageSize: number;
  pageSizeOptions: number[];
  /** Null while the total is unknown — page numbers are hidden then. */
  pageCount: number | null;
  totalCount: number | null;
  /** Rows rendered on the current page; the pager hides itself when zero. */
  itemCount: number;
  /** Only consulted when `pageCount` is null. */
  hasNext?: boolean;
  disabled?: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  labels: DataTablePaginationLabels;
  className?: string;
}

/** First, last and the current page ±1 — the gaps collapse into an ellipsis. */
export function buildPageItems(
  page: number,
  pageCount: number,
): (number | 'ellipsis')[] {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }
  const pages = new Set([1, pageCount, page, page - 1, page + 1]);
  // A lone hidden page between two shown ones reads worse than the page itself
  if (page <= 4) [2, 3, 4, 5].forEach((p) => pages.add(p));
  if (page >= pageCount - 3)
    [pageCount - 4, pageCount - 3, pageCount - 2, pageCount - 1].forEach((p) =>
      pages.add(p),
    );

  const sorted = [...pages]
    .filter((p) => p >= 1 && p <= pageCount)
    .sort((a, b) => a - b);
  const items: (number | 'ellipsis')[] = [];
  let previous = 0;
  for (const current of sorted) {
    if (previous && current - previous > 1) items.push('ellipsis');
    items.push(current);
    previous = current;
  }
  return items;
}

export function DataTablePagination({
  page,
  pageSize,
  pageSizeOptions,
  pageCount,
  totalCount,
  itemCount,
  hasNext = false,
  disabled = false,
  onPageChange,
  onPageSizeChange,
  labels,
  className,
}: DataTablePaginationProps) {
  if (itemCount === 0) return null;

  const from = (page - 1) * pageSize + 1;
  const to = from + itemCount - 1;
  const canPrevious = page > 1;
  const canNext = pageCount === null ? hasNext : page < pageCount;

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 text-sm',
        className,
      )}
    >
      <span className="text-muted-foreground">
        {totalCount === null
          ? labels.pageOnly(page)
          : labels.range(from, to, totalCount)}
      </span>

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-muted-foreground">
          <span className="hidden sm:inline">{labels.rowsPerPage}</span>
          <Select
            options={pageSizeOptions.map((size) => ({
              value: String(size),
              label: String(size),
            }))}
            value={String(pageSize)}
            onChange={(value) => onPageSizeChange(Number(value))}
            disabled={disabled}
            size="sm"
            className="w-20"
            aria-label={labels.rowsPerPage}
          />
        </label>

        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            onClick={() => onPageChange(page - 1)}
            disabled={disabled || !canPrevious}
            aria-label={labels.previous}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {pageCount !== null &&
            buildPageItems(page, pageCount).map((item, index) =>
              item === 'ellipsis' ? (
                <span
                  key={`ellipsis-${index}`}
                  className="px-1 text-muted-foreground"
                  aria-hidden
                >
                  …
                </span>
              ) : (
                <Button
                  key={item}
                  type="button"
                  variant={item === page ? 'contained-teal' : 'outline'}
                  size="icon-sm"
                  onClick={() => onPageChange(item)}
                  disabled={disabled}
                  aria-label={labels.goToPage(item)}
                  aria-current={item === page ? 'page' : undefined}
                >
                  {item}
                </Button>
              ),
            )}

          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            onClick={() => onPageChange(page + 1)}
            disabled={disabled || !canNext}
            aria-label={labels.next}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
